/**
 * Hotline - spec 23, the people members can call from Home → Quick help.
 *
 * Every change is live on the member's next request (NFR-10.6): there is no
 * publish step, so the table here is exactly what members see, in the same
 * order. Delete is a real delete; the contact's photo goes with it.
 *
 * The photo is its own request, as on the partner dialog: it is enabled once
 * the contact exists, and a storage outage never loses the details already
 * saved. It is cropped square in the browser, because members see it in a circle.
 */

import { useRef, useState } from "react";

import { DataTable, PrimaryCell, type Column } from "../components/DataTable";
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  LoadingRows,
  Modal,
  Page,
  SuccessNotice,
} from "../components/primitives";
import {
  createHotlineContact,
  deleteHotlineContact,
  fetchHotlineContacts,
  removeHotlinePhoto,
  reorderHotlineContacts,
  updateHotlineContact,
  uploadHotlinePhoto,
  type HotlineContact,
  type HotlineWrite,
} from "../lib/api/hotline";
import { confirmAction, useMutation, useQuery } from "../lib/hooks";
import { orDash } from "../lib/format";
import { formatBytes, optimizePhoto } from "../lib/image";

const PHOTO_OPTIONS = { aspect: 1, maxWidth: 800 };

export function HotlineScreen() {
  const [editing, setEditing] = useState<{ contact: HotlineContact | null } | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const { data, error, loading, refreshing, reload } = useQuery(() => fetchHotlineContacts(), []);
  const contacts = data?.items ?? [];

  const remove = useMutation((contact: HotlineContact) => deleteHotlineContact(contact.id), {
    onSuccess: (result) => {
      setSaved(result.message);
      reload();
    },
  });

  /** Swap a contact with its neighbour, then send the whole list renumbered
   *  0..n-1, so earlier gaps or ties in `display_order` are cleaned up too. */
  const move = useMutation(
    (index: number, direction: -1 | 1) => {
      const order = contacts.map((contact) => contact.id);
      const [moved] = order.splice(index, 1);
      order.splice(index + direction, 0, moved as string);
      return reorderHotlineContacts(order.map((id, position) => ({ id, display_order: position })));
    },
    { onSuccess: () => reload() },
  );

  const busy = remove.pending || move.pending || refreshing;

  const columns: Array<Column<HotlineContact>> = [
    {
      key: "name",
      header: "Contact",
      render: (row) => (
        <div className="row row--tight">
          <Avatar name={row.name} url={row.photo_url} />
          <PrimaryCell title={row.name} detail={row.role ?? undefined} />
        </div>
      ),
    },
    { key: "phone", header: "Phone", render: (row) => row.phone },
    { key: "email", header: "Email", render: (row) => orDash(row.email) },
    {
      key: "order",
      header: "Order",
      render: (row) => {
        const index = contacts.indexOf(row);
        return (
          <div className="row row--tight">
            <Button
              size="sm"
              variant="quiet"
              aria-label={`Move ${row.name} up`}
              disabled={busy || index === 0}
              onClick={() => void move.run(index, -1)}
            >
              ↑
            </Button>
            <Button
              size="sm"
              variant="quiet"
              aria-label={`Move ${row.name} down`}
              disabled={busy || index === contacts.length - 1}
              onClick={() => void move.run(index, 1)}
            >
              ↓
            </Button>
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div className="table__actions">
          <Button size="sm" onClick={() => setEditing({ contact: row })}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() => {
              if (confirmAction(`Remove "${row.name}" from the hotline? Members stop seeing them straight away.`)) {
                void remove.run(row);
              }
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Page>
      {saved ? <SuccessNotice message={saved} onDismiss={() => setSaved(null)} /> : null}
      {remove.error ? <ErrorNotice error={remove.error} /> : null}
      {move.error ? <ErrorNotice error={move.error} /> : null}

      <Card>
        <div className="row row--tight" style={{ justifyContent: "space-between" }}>
          <span className="text-small">Members see these contacts in this order on the Fitzone Hotline screen.</span>
          <div className="row row--tight">
            <Button onClick={reload} disabled={refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
            <Button variant="accent" onClick={() => setEditing({ contact: null })}>
              New contact
            </Button>
          </div>
        </div>
      </Card>

      {error ? <ErrorNotice error={error} onRetry={reload} /> : null}

      <Card flush>
        {loading && !data ? (
          <LoadingRows rows={3} columns={5} />
        ) : contacts.length === 0 ? (
          <EmptyState
            title="No hotline contacts yet."
            body="Members see an empty Hotline screen until you add someone."
            action={
              <Button size="sm" variant="accent" onClick={() => setEditing({ contact: null })}>
                New contact
              </Button>
            }
          />
        ) : (
          <DataTable columns={columns} rows={contacts} rowKey={(row) => row.id} />
        )}
      </Card>

      {editing ? (
        <HotlineFormDialog
          contact={editing.contact}
          onClose={() => setEditing(null)}
          onPhotoSaved={reload}
          onDone={(message) => {
            setEditing(null);
            setSaved(message);
            reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function HotlineFormDialog({
  contact,
  onClose,
  onPhotoSaved,
  onDone,
}: {
  contact: HotlineContact | null;
  onClose: () => void;
  /** The photo is saved on its own request, so the list behind the dialog is reloaded then too. */
  onPhotoSaved: () => void;
  onDone: (message: string) => void;
}) {
  const isNew = contact === null;
  const [form, setForm] = useState({
    name: contact?.name ?? "",
    role: contact?.role ?? "",
    phone: contact?.phone ?? "",
    email: contact?.email ?? "",
  });
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  /** Set once the row exists - on edit immediately, on create after the first save. */
  const [savedId, setSavedId] = useState<string | null>(contact?.id ?? null);

  function payload(): HotlineWrite {
    return {
      name: form.name.trim(),
      phone: form.phone.trim(),
      role: form.role.trim() || null,
      email: form.email.trim() || null,
    };
  }

  const save = useMutation(
    () => (savedId ? updateHotlineContact(savedId, payload()) : createHotlineContact(payload())),
    {
      onSuccess: (result) => {
        setSavedId(result.contact.id);
        if (contact) {
          onDone(result.message);
          return;
        }
        // A create: keep the dialog open so a photo can be attached to the row
        // that now exists.
        setSavedNotice(`${result.message} Add a photo now, or close - it can be added later.`);
        onPhotoSaved();
      },
    },
  );

  const canSave = form.name.trim() !== "" && form.phone.trim() !== "" && !save.pending;

  return (
    <Modal
      title={isNew ? "New hotline contact" : `Edit ${contact.name}`}
      description="Members can call or email this person from the Fitzone Hotline screen."
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 16 }}>
        <div className="form-grid">
          <Field label="Name (required)">
            {(id) => (
              <Input
                id={id}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                maxLength={120}
              />
            )}
          </Field>
          <Field label="Role" hint="Shown under the name, e.g. Owner, Fitzone.">
            {(id) => (
              <Input
                id={id}
                value={form.role}
                onChange={(event) => setForm({ ...form, role: event.target.value })}
                maxLength={120}
              />
            )}
          </Field>
          <Field label="Phone (required)" hint="Written as members should see it, e.g. +977 985-1354488.">
            {(id) => (
              <Input
                id={id}
                type="tel"
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
                maxLength={32}
              />
            )}
          </Field>
          <Field label="Email" hint="Optional. Members get an Email button when set.">
            {(id) => (
              <Input
                id={id}
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                maxLength={254}
              />
            )}
          </Field>
        </div>

        <HotlinePhotoField
          contactId={savedId}
          name={form.name || contact?.name || ""}
          initialUrl={contact?.photo_url ?? null}
          onSaved={onPhotoSaved}
        />

        <ErrorNotice error={save.error} />
        {savedNotice ? <SuccessNotice message={savedNotice} onDismiss={() => setSavedNotice(null)} /> : null}

        <div className="modal__actions">
          <Button onClick={onClose} disabled={save.pending}>
            {savedId && !contact ? "Close" : "Cancel"}
          </Button>
          <Button variant="accent" disabled={!canSave} onClick={() => void save.run()}>
            {save.pending ? "Saving…" : savedId && !contact ? "Save changes" : "Save contact"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type PhotoBusy = "Optimizing…" | "Uploading…" | "Removing…";

/** One square photo. Cropped, resized and re-encoded in the browser first. */
function HotlinePhotoField({
  contactId,
  name,
  initialUrl,
  onSaved,
}: {
  contactId: string | null;
  name: string;
  initialUrl: string | null;
  onSaved: () => void;
}) {
  const [url, setUrl] = useState<string | null>(initialUrl);
  const [busy, setBusy] = useState<PhotoBusy | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    if (!contactId) return;
    setError(null);
    setNotice(null);
    try {
      setBusy("Optimizing…");
      const optimized = await optimizePhoto(file, PHOTO_OPTIONS);
      setBusy("Uploading…");
      const result = await uploadHotlinePhoto(contactId, optimized.blob, optimized.fileName);
      setUrl(result.contact.photo_url);
      setNotice(
        `${result.message} Optimised from ${formatBytes(optimized.before)} to ${formatBytes(optimized.after)} ` +
          `(${optimized.width}×${optimized.height}).`,
      );
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!contactId || !confirmAction("Remove this photo? Members see initials instead.")) return;
    setError(null);
    setNotice(null);
    try {
      setBusy("Removing…");
      const result = await removeHotlinePhoto(contactId);
      setUrl(result.contact.photo_url);
      setNotice(result.message);
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Field
      label="Photo"
      hint={
        contactId
          ? "Shown in a circle. Cropped square, resized to 800px and compressed before upload."
          : "Save the contact first, then add a photo."
      }
    >
      {(id) => (
        <div id={id} className="stack" style={{ gap: 10 }} role="group" aria-label="Contact photo">
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared so choosing the same file again still fires `change`.
              event.target.value = "";
              if (file) void upload(file);
            }}
          />
          <div className="photo-slot" style={{ width: 160, maxWidth: "100%" }}>
            <div className="photo-slot__frame" style={{ aspectRatio: "1 / 1", borderRadius: "50%" }}>
              {url ? (
                <img src={url} alt={name ? `${name}'s photo` : "Contact photo"} loading="lazy" />
              ) : (
                <Avatar name={name || "?"} />
              )}
              {busy ? <span className="photo-slot__busy">{busy}</span> : null}
            </div>
            <div className="row row--tight">
              <Button size="sm" disabled={!contactId || busy !== null} onClick={() => input.current?.click()}>
                {url ? "Replace" : "Add photo"}
              </Button>
              {url ? (
                <Button size="sm" variant="quiet" disabled={busy !== null} onClick={() => void remove()}>
                  Remove
                </Button>
              ) : null}
            </div>
          </div>
          <ErrorNotice error={error} />
          {notice ? <SuccessNotice message={notice} onDismiss={() => setNotice(null)} /> : null}
        </div>
      )}
    </Field>
  );
}
